import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC } from 'react';

import { PermissionsTypes } from '@gem/call/Model/callPermissions/callPermissionsModel.types';
import { CallAccessForm } from '@gem/call/popups/Permissions/CallPermissionsPopup/components/CallAccessForm/CallAccessForm';
import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps, Popup } from '@gem/popups';
import { platformInfo } from '@gem/utils';

import { CallMemberPermission } from '../Shared/CallMemberPermission/CallMemberPermission';
import { CallPermission } from '../Shared/CallPermission/CallPermission';
import { useCallPermissionsPopupPresenter } from './CallPermissionsPopup.presenter';
import styles from './CallPermissionsPopup.styles.m.css';
import { ICallPermissionsPopupProps } from './CallPermissionsPopup.types';
import { CallCustomPermissionsBlock } from './components/CallCustomPermissionsBlock/CallCustomPermissionsBlock';
import { CallSpeakerListBlock } from './components/CallSpeakerListBlock/CallSpeakerListBlock';

export const CallPermissionsPopup: FC<ICommonPopupsProps<ICallPermissionsPopupProps>> = observer(
  (props) => {
    const { call, callController } = props.params!;
    const { t } = useTranslation();
    const presenter = useCallPermissionsPopupPresenter(props);

    return (
      <Popup
        isHidden={props.popupInstance.isHidden}
        overlayContainerClassName={styles.overlayingPopup}
        className={classNames(styles.popup, { [styles.mobileBorder]: platformInfo.isMobile })}
        title={t(
          presenter.canShowAccessForm ? 'calls_kebab_access_permissens_button' : 'permissions_label'
        )}
        isOpened
        onClose={presenter.onClose}
      >
        <div className={styles.wrapper}>
          {presenter.canShowAccessForm ? (
            <>
              <div className={styles.blockName}>{t('calls_access_permissens_access_header')}</div>
              <CallAccessForm
                call={call}
                callController={callController}
              />
              <div className={styles.blockDivider} />
            </>
          ) : null}

          {presenter.isShowWebinarPermissions && (
            <CallSpeakerListBlock
              popupInstance={props.popupInstance}
              onClose={presenter.onClose}
              call={call}
              callController={callController}
            />
          )}

          {presenter.isShowCustomPermissions && (
            <>
              <div className={styles.blockName}>
                {t(presenter.isViewOnly ? 'call_what_can_i_do' : 'call_what_can_participants_do')}
              </div>

              {presenter.isViewOnly && (
                <>
                  <CallMemberPermission
                    callController={callController}
                    call={call}
                    popupInstance={props.popupInstance}
                    onClose={presenter.onClose}
                    permissionType={PermissionsTypes.SwitchOnCamera}
                  />
                  <CallMemberPermission
                    callController={callController}
                    call={call}
                    popupInstance={props.popupInstance}
                    onClose={presenter.onClose}
                    permissionType={PermissionsTypes.SwitchOnMic}
                  />
                  <CallMemberPermission
                    callController={callController}
                    call={call}
                    popupInstance={props.popupInstance}
                    onClose={presenter.onClose}
                    permissionType={PermissionsTypes.ShareScreen}
                  />
                </>
              )}

              {!presenter.isViewOnly && (
                <>
                  <CallPermission
                    callController={callController}
                    call={call}
                    popupInstance={props.popupInstance}
                    onClose={presenter.onClose}
                    permissionType={PermissionsTypes.SwitchOnCamera}
                  />
                  <CallPermission
                    callController={callController}
                    call={call}
                    popupInstance={props.popupInstance}
                    onClose={presenter.onClose}
                    permissionType={PermissionsTypes.SwitchOnMic}
                  />
                  <CallPermission
                    callController={callController}
                    call={call}
                    popupInstance={props.popupInstance}
                    onClose={presenter.onClose}
                    permissionType={PermissionsTypes.ShareScreen}
                  />
                  <CallCustomPermissionsBlock
                    popupInstance={props.popupInstance}
                    onClose={presenter.onClose}
                    call={call}
                    callController={callController}
                  />
                </>
              )}
            </>
          )}
        </div>
      </Popup>
    );
  }
);
